import type { LevelPoint } from '../../store/selectors'

/**
 * A skill's level over a window, in a few pixels. Levels are rungs 1–5, so
 * the line is drawn as steps: a level holds until it moves. Nothing is
 * interpolated that did not happen.
 */
export function Sparkline({
  points, from, to, color, width = 84, height = 22, target,
}: {
  points: LevelPoint[]
  from: number
  to: number
  color?: string
  width?: number
  height?: number
  /** Dashed reach from the last point to a target at `to`. */
  target?: number
}) {
  const span = Math.max(1, to - from)
  const x = (t: number) => Math.max(0, Math.min(width, ((t - from) / span) * width))
  const y = (l: number) => height - 2 - ((Math.max(1, Math.min(5, l)) - 1) / 4) * (height - 4)

  // Carry the last known level before the window in as the starting value.
  let start: number | undefined
  const inWindow: LevelPoint[] = []
  for (const p of points) {
    if (p.at < from) start = p.level
    else inWindow.push(p)
  }
  const seq: Array<{ at: number; level: number }> = []
  if (start !== undefined) seq.push({ at: from, level: start })
  for (const p of inWindow) seq.push(p)
  if (seq.length === 0) return <svg className="spark" width={width} height={height} aria-hidden="true" />

  const last = seq[seq.length - 1]!
  const d: string[] = []
  seq.forEach((p, i) => {
    if (i === 0) d.push(`M${x(p.at).toFixed(1)},${y(p.level).toFixed(1)}`)
    else {
      const prev = seq[i - 1]!
      d.push(`H${x(p.at).toFixed(1)}`)
      if (p.level !== prev.level) d.push(`V${y(p.level).toFixed(1)}`)
    }
  })
  const nowX = x(Math.min(Date.now(), to))
  d.push(`H${nowX.toFixed(1)}`)

  return (
    <svg className="spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={d.join(' ')} fill="none" stroke={color ?? 'currentColor'} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
      {typeof target === 'number' && target !== last.level && (
        <path
          d={`M${nowX.toFixed(1)},${y(last.level).toFixed(1)} L${width},${y(target).toFixed(1)}`}
          fill="none" stroke={color ?? 'currentColor'} strokeWidth={1.25} strokeDasharray="2.5 3" opacity={0.7}
        />
      )}
      <circle cx={nowX} cy={y(last.level)} r={2.2} fill={color ?? 'currentColor'} />
    </svg>
  )
}
